import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_DEFAULTS, pageDescription, pageKeywords } from './pageMeta';
import {
  type PageType,
  type ProjectData,
  type ArticleData,
  websiteSchema,
  organizationSchema,
  faqSchema,
  blogSchema,
  projectsCollectionSchema,
  videoSchema,
  breadcrumbSchema,
  individualProjectSchema,
  blogPostingSchema,
} from './schemas';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  pageType?: PageType;
  customSchema?: object;
  projectData?: ProjectData;
  articleData?: ArticleData;
}

/**
 * Central meta + structured-data component. Per-page copy lives in
 * ./pageMeta, JSON-LD builders in ./schemas; this component only resolves
 * which of them apply to the current page and renders the head tags.
 */
const SEO: React.FC<SEOProps> = ({
  title = SEO_DEFAULTS.title,
  description = SEO_DEFAULTS.description,
  keywords = SEO_DEFAULTS.keywords,
  image = SEO_DEFAULTS.image,
  url = SEO_DEFAULTS.url,
  type = SEO_DEFAULTS.type,
  pageType = 'home',
  customSchema,
  projectData,
  articleData,
}) => {
  const currentDescription = pageDescription(pageType, projectData) || description;
  const pageKw = pageKeywords(pageType, projectData);
  const currentKeywords = pageKw ? `${pageKw}, ${keywords}` : keywords;

  const showPortfolioSchemas = pageType === 'home' || pageType === 'projects';

  const allStructuredData = [
    websiteSchema,
    organizationSchema,
    breadcrumbSchema(pageType, articleData, projectData),
    ...(pageType === 'home' ? [faqSchema] : []),
    ...(pageType === 'blog' ? [blogSchema] : []),
    ...(showPortfolioSchemas ? [projectsCollectionSchema] : []),
    ...(projectData ? [individualProjectSchema(projectData)] : []),
    ...(customSchema ? [customSchema] : []),
    ...(articleData ? [blogPostingSchema(articleData)] : []),
    ...(showPortfolioSchemas ? [videoSchema] : []),
  ];

  return (
    <Helmet>
      {/* Enhanced Title Strategy */}
      <title>{title}</title>
      <meta name="description" content={currentDescription} />
      <meta name="keywords" content={currentKeywords} />
      <meta name="author" content="Talal Alkhaled" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

      {/* Enhanced Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Advanced SEO Meta Tags */}
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="HandheldFriendly" content="true" />
      <meta name="MobileOptimized" content="width" />

      {/* Google Search Console Verification */}
      <meta name="google-site-verification" content="4laJrbEfkCVMRNGIf25oLG8V23L_LecqCP4hCabVHhc" />

      {/* Enhanced Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={currentDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/webp" />
      <meta property="og:image:alt" content="Talal Alkhaled - Computer Engineer & Developer" />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Talal Alkhaled Portfolio" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="ar_SA" />

      {/* Enhanced Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={currentDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content="Talal Alkhaled - Computer Engineer & Developer" />
      <meta name="twitter:creator" content="@talalal5aled" />
      <meta name="twitter:site" content="@talalal5aled" />

      {/* Additional Professional Meta Tags */}
      <meta name="theme-color" content="#3B82F6" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      <meta name="application-name" content="Talal Alkhaled Portfolio" />
      <meta name="apple-mobile-web-app-title" content="Talal Alkhaled" />

      {/* Language and Region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="KW" />
      <meta name="geo.placename" content="Kuwait" />
      <meta name="geo.position" content="29.3759;47.9774" />
      <meta name="ICBM" content="29.3759, 47.9774" />

      {/* Professional Schema Hints */}
      <meta name="classification" content="Portfolio, Computer Engineering, Web Development, AI Integration" />
      <meta name="category" content="Technology, Education, Development, Engineering" />
      <meta name="coverage" content="Worldwide" />
      <meta name="subject" content="Computer Engineering, AI, Web Development, Mobile Apps" />

      {/* Performance and Security */}
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="format-detection" content="telephone=no" />

      {/* Mobile and Accessibility */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />

      {/* DNS Prefetch for Performance */}
      <link rel="dns-prefetch" href="//api.openai.com" />
      <link rel="dns-prefetch" href="//github.com" />
      <link rel="dns-prefetch" href="//linkedin.com" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

      {/* Video-specific Open Graph tags for pages with the showcase video */}
      {showPortfolioSchemas && <meta property="og:video" content="https://talkhaled.com/RecentWork.mp4" />}
      {showPortfolioSchemas && <meta property="og:video:type" content="video/mp4" />}
      {showPortfolioSchemas && <meta property="og:video:width" content="1920" />}
      {showPortfolioSchemas && <meta property="og:video:height" content="1080" />}
      {showPortfolioSchemas && <meta property="og:video:duration" content="225" />}

      {/* Rich Snippets Support */}
      <meta name="news_keywords" content="Computer Engineering, AI Integration, Web Development, Mobile Apps, Technology Innovation" />

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(allStructuredData)}</script>
    </Helmet>
  );
};

export default SEO;
